import React from 'react';

import type { GenericDetails } from 'components/Input/Autocomplete';
import { useDebouncedFilterInput } from 'components/Toolbar/hooks/useDebouncedFilterInput';

import CheckIcon from '@mui/icons-material/Check';
import type { AutocompleteProps } from '@mui/material';
import { Autocomplete, Box, Grid2, TextField, useTheme } from '@mui/material';

import { DropdownBox } from '../DropdownBox';
import match from 'autosuggest-highlight/match';
import parse from 'autosuggest-highlight/parse';

interface SingleSelectProps<T extends GenericDetails>
    extends Omit<AutocompleteProps<T, boolean | undefined, boolean | undefined, boolean | undefined>, 'inputValue' | 'renderInput'> {
    options: T[];
    selectedItem?: T | null;
    setSelectedItem: (item: T | null) => void;
    onClear?: () => void;
    handleClose?: () => void;
    name?: string;
    placeholder?: string;
    label?: string;
    isLoading?: boolean;
}

const SingleSelect = React.memo(
    <T extends GenericDetails>({
        selectedItem,
        options,
        setSelectedItem,
        onClear,
        handleClose,
        placeholder,
        isLoading,
        name,
        label,
        ...rest
    }: SingleSelectProps<T>) => {
        const { sortedOptions, handleChange, setLocalInputValue } = useDebouncedFilterInput({
            options,
            selectedItem,
            setSelectedItem,
            onClear,
            handleClose,
        });

        const theme = useTheme();

        return (
            <Autocomplete
                open
                data-testid={`${name}_search_filter`}
                data-locator={`${name}_single_select`}
                sx={{ width: '100%', minWidth: '200px', maxWidth: '100%', p: 2 }}
                getOptionLabel={(option) => (typeof option === 'string' ? option : option.name)}
                options={sortedOptions}
                autoComplete
                includeInputInList
                value={selectedItem ?? null}
                isOptionEqualToValue={(option, value) => option.id === value.id}
                noOptionsText="No results"
                // @ts-expect-error - Fix this later
                onChange={handleChange}
                onInputChange={(_event, newInputValue) => {
                    if (newInputValue) {
                        setLocalInputValue(newInputValue);
                    }
                }}
                forcePopupIcon={false}
                disableClearable
                disableListWrap
                disabledItemsFocusable
                renderInput={(params) => {
                    return (
                        <TextField
                            {...params}
                            // eslint-disable-next-line jsx-a11y/no-autofocus
                            autoFocus
                            label={label}
                            placeholder={placeholder}
                            fullWidth
                            sx={{
                                '& .MuiInputBase-root': {
                                    height: '42px',
                                    py: 0,
                                },
                            }}
                        />
                    );
                }}
                disablePortal
                renderOption={(props, option, { inputValue, selected }) => {
                    const matches = match(option.name, inputValue, { insideWords: true });
                    const parts = parse(option.name, matches);

                    return (
                        <li {...props}>
                            <Grid2 container alignItems="center" justifyContent="space-between">
                                <Box
                                    display="flex"
                                    sx={{
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                        display: '-webkit-box',
                                        WebkitBoxOrient: 'vertical',
                                        WebkitLineClamp: 4,
                                        whiteSpace: 'pre-line',
                                    }}
                                >
                                    {parts.map((part, index) => {
                                        return (
                                            <span
                                                key={`${String(part.text)}${index}`}
                                                style={{
                                                    fontWeight: part.highlight ? 700 : 400,
                                                    color: part.highlight ? theme.palette.common.black : 'black',
                                                }}
                                            >
                                                {part.text}
                                            </span>
                                        );
                                    })}
                                </Box>
                                {selected && (
                                    <Grid2 display="flex" alignSelf="flex-end" justifyItems="center">
                                        <CheckIcon color="primary" />
                                    </Grid2>
                                )}
                            </Grid2>
                        </li>
                    );
                }}
                {...rest}
                slots={{
                    popper: (props) => (
                        <DropdownBox
                            {...props}
                            style={{
                                width: 'unset',
                            }}
                            maxItems={sortedOptions.length}
                            isLoading={isLoading}
                            isFetched={sortedOptions.length > 0}
                        />
                    ),
                }}
            />
        );
    }
);

SingleSelect.displayName = 'SingleSelect';

export { SingleSelect };
